import type { ObjectWithId } from "@/types";

export const isValidObject = (value?: unknown): value is object =>
  value && typeof value === "object" && !Array.isArray(value) ? true : false;

export const isEmptyObject = (value: unknown) => {
  if (!isValidObject(value)) return false;
  return Object.keys(value).length <= 0;
};

export const getObjectKeys = <Obj extends object>(
  obj: Obj
): Array<keyof Obj> => {
  // Object.keys returns string[] by default, so we force the type to Array<keyof T>
  return Object.keys(obj) as Array<keyof Obj>;
};

export const isObjectWithId = (value: unknown): value is ObjectWithId => {
  return isValidObject(value) && "id" in value;
};

export const getObjectId = <Item = unknown>(item?: Item | null) => {
  return isObjectWithId(item) ? item.id : undefined;
};

export const omitKeysBy = (obj = {}, omitFn: (key: string) => boolean) => {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => omitFn(key)));
};

export const getObjectKeyValue = (
  obj: object,
  key: string | number | symbol
) => {
  return isKeyOfObject(obj, key) ? obj[key] : undefined;
};

export const isKeyOfObject = (
  obj: object,
  key: string | number | symbol
): key is keyof object => key in obj;

export const removeEmptyValuesFromObject = (obj: object): object => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => value !== "" && value !== null)
      .map(([key, value]) => [
        key,
        value === Object(value) ? removeEmptyValuesFromObject(value) : value,
      ])
  );
};

export function isValueObject(
  value: unknown
): value is { value?: string | number | null } {
  return isValidObject(value) && "value" in value;
}
